import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, KundeAkte, Zuordnung } from "@kundeninfo/api-client";
import { Arbeitsdialog, Feld, Modal, ModalFuss, darfAbbrechen, useBeforeUnload, useBestaetigung, useFormularZustand,
  useSofortPopup } from "@kundeninfo/ui";
import { Auswahl, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { fieldErrorInsteadOf409 } from "../../components/fieldErrors.js";
import { PARTNER_TEXT } from "./texts.js";

/**
 * „ROLLEN JE UNTERNEHMEN“ (Spec „Wortwahl“, Reiter Stammdaten › Beziehungen; Lieferanten Strang A, Aufgabe 6): je
 * Beziehung die Rollen ändern (Modalpanel rechts; der Speichern-Knopf ist die Bestätigung, Dirk 216c) und die
 * Kreditornummer pflegen (Abschnitt „Nummern“). Die Bausteine kennen nur EINE Beziehung; `KundeBeziehungen` stellt sie je Zeile auf und lädt danach die
 * Akte neu (die Schreibwege antworten nur mit der Beziehung bzw. der Nummer).
 */

export type RelationRolesClient = Pick<ApiClient, "getCustomer" | "setRelationRoles" | "setKreditornummer">;

type RelationView = { client: RelationRolesClient; kunde: KundeAkte; relation: Zuordnung; onAktualisiert: (akte: KundeAkte) => void };

/** Ein Riegel gegen Antworten nach dem Aushängen (Kundenwechsel räumt die Zeilen ab, Muster `lauf` in KundeBeziehungen). */
function useMounted(): { current: boolean } {
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  return mounted;
}

type RolesDraft = { status: Zuordnung["status"]; supplierStatus: Zuordnung["supplierStatus"]; customerNumber: string };

const CUSTOMER_STAGES = ["PROSPECT", "ACTIVE", "DORMANT", "FORMER"] as const;
const CUSTOMER_STAGE_TEXT: Record<(typeof CUSTOMER_STAGES)[number], string> = {
  PROSPECT: PARTNER_TEXT.badge.PROSPECT, ACTIVE: PARTNER_TEXT.badge.CUSTOMER,
  DORMANT: PARTNER_TEXT.badge.DORMANT, FORMER: PARTNER_TEXT.badge.FORMER,
};

/**
 * „Rollen ändern“ an einer Beziehung: Kundenrolle (keine, Interessent, Kunde, Ruhend, Ehemalig) und Lieferantenrolle
 * (keine, Lieferant, ehem. Lieferant). Mindestens eine bleibt gesetzt — sonst ist „Speichern“ gesperrt, das
 * Sofort-Popup sagt warum. Bekommt die Beziehung die Kundenrolle neu und hatte nie eine Nummer, darf eine von Hand
 * mit (C7), sonst vergibt der Server; hatte sie schon eine, sagt eine Lesezeile, dass sie wieder genutzt wird. Hat die
 * entfernte Rolle schon Vorgänge, antwortet der Server 409 `ROLLE_HAT_VORGAENGE` — der Satz steht am Feld, der Ausweg
 * ist „Ehemalig“. Keine zweite Frage vor dem Schreiben: Der Speichern-Knopf des Dialogs ist die Bestätigung (Dirk 216c).
 */
export function ChangeRoles(p: RelationView & { gesperrt: boolean }): ReactElement {
  const r = p.relation;
  const [entwurf, setEntwurf] = useState<RolesDraft | null>(null);
  const mounted = useMounted();
  const kundenrolleZurueck = entwurf !== null && r.status === null && entwurf.status !== null;
  const neueKundenrolle = kundenrolleZurueck && r.customerNumber === null;
  const gewaehlt = entwurf !== null && (entwurf.status !== null || entwurf.supplierStatus !== null);
  const formular = useFormularZustand<KundeAkte | null>({
    speichern: async () => {
      if (entwurf === null) return null;
      const nummer = entwurf.customerNumber.trim();
      await fieldErrorInsteadOf409(() => p.client.setRelationRoles(p.kunde.id, r.id, {
        version: r.version, status: entwurf.status, supplierStatus: entwurf.supplierStatus,
        ...(neueKundenrolle && nummer !== "" ? { customerNumber: nummer } : {}),
      }), ["ROLLE_HAT_VORGAENGE"]);
      return p.client.getCustomer(p.kunde.id);
    },
    // Versionskonflikt: der frische Stand kommt über die Akte (neue `version` an der Beziehung), der Entwurf folgt ihm.
    neuLaden: async () => {
      const frisch = await p.client.getCustomer(p.kunde.id);
      if (!mounted.current) return;
      p.onAktualisiert(frisch);
      const neu = frisch.relations.find((x) => x.id === r.id);
      setEntwurf(neu === undefined ? null : { status: neu.status, supplierStatus: neu.supplierStatus, customerNumber: "" });
    },
    bekannteFelder: ["status", "supplierStatus", "customerNumber"],
  });
  const speichernHinweis = useSofortPopup<HTMLElement>(gewaehlt ? "" : PARTNER_TEXT.atLeastOne);
  const geaendert = entwurf !== null
    && (entwurf.status !== r.status || entwurf.supplierStatus !== r.supplierStatus || entwurf.customerNumber !== "");
  useBeforeUnload(geaendert);

  function oeffnen(): void {
    setEntwurf({ status: r.status, supplierStatus: r.supplierStatus, customerNumber: "" });
    formular.zuruecksetzen();
  }

  async function abbrechen(): Promise<void> {
    if (formular.laeuft) return;
    if (!(await darfAbbrechen(geaendert))) return;
    setEntwurf(null);
  }

  async function speichern(): Promise<void> {
    if (!gewaehlt || formular.laeuft) return;
    const frisch = await formular.aktion();
    if (frisch === undefined || frisch === null || !mounted.current) return;
    setEntwurf(null);
    p.onAktualisiert(frisch);
  }

  const setze = (teil: Partial<RolesDraft>): void => setEntwurf((e) => (e === null ? e : { ...e, ...teil }));
  return (
    <>
      <KiKnopf name={PARTNER_TEXT.changeRolesAt(r.shortCode)} deaktiviert={p.gesperrt || entwurf !== null}
        onClick={oeffnen}>
        {PARTNER_TEXT.changeRoles}
      </KiKnopf>
      {entwurf !== null && (
        <Arbeitsdialog titel={PARTNER_TEXT.rolesDialog(p.kunde.companyName, r.shortCode)} panel="partner-rollen"
          onAbbrechen={abbrechen} abbrechenGesperrt={formular.laeuft}
          meldungen={formular.meldung === null ? undefined : <Hinweis art="fehler">{formular.meldung}</Hinweis>}
          fuss={(
            <>
              <KiKnopf art="primaer" deaktiviert={formular.laeuft || !gewaehlt} knopfRef={speichernHinweis}
                onClick={() => { void speichern(); }}>
                {PARTNER_TEXT.save}
              </KiKnopf>
              <KiKnopf deaktiviert={formular.laeuft} onClick={() => { void abbrechen(); }}>{PARTNER_TEXT.cancel}</KiKnopf>
            </>
          )}>
          <Formular beschriftung="oben" {...formular.formularProps}>
            <Auswahl id="prr-status" name="prr-status" beschriftung={PARTNER_TEXT.customerRole} wert={entwurf.status ?? ""}
              {...(formular.feldFehler.status === undefined ? {} : { fehler: formular.feldFehler.status })}
              onGeaendert={(d) => setze({ status: d.wert === "" ? null : d.wert as RolesDraft["status"] })}>
              <option value="">{PARTNER_TEXT.none}</option>
              {CUSTOMER_STAGES.map((s) => <option key={s} value={s}>{CUSTOMER_STAGE_TEXT[s]}</option>)}
            </Auswahl>
            {kundenrolleZurueck && r.customerNumber !== null && (
              <p className="kbz__hinweis">{PARTNER_TEXT.customerNumberReused(r.customerNumber)}</p>
            )}
            {neueKundenrolle && (
              <Feld id="prr-customerNumber" label={PARTNER_TEXT.customerNumber} wert={entwurf.customerNumber} bearbeitbar
                hilfe={PARTNER_TEXT.customerNumberHint} fehler={formular.feldFehler.customerNumber}
                onChange={(wert) => setze({ customerNumber: wert })} />
            )}
            <Auswahl id="prr-supplierStatus" name="prr-supplierStatus" beschriftung={PARTNER_TEXT.supplierRole}
              wert={entwurf.supplierStatus ?? ""}
              {...(formular.feldFehler.supplierStatus === undefined ? {} : { fehler: formular.feldFehler.supplierStatus })}
              onGeaendert={(d) => setze({ supplierStatus: d.wert === "" ? null : d.wert as RolesDraft["supplierStatus"] })}>
              <option value="">{PARTNER_TEXT.none}</option>
              <option value="ACTIVE">{PARTNER_TEXT.badge.SUPPLIER}</option>
              <option value="FORMER">{PARTNER_TEXT.badge.SUPPLIER_FORMER}</option>
            </Auswahl>
          </Formular>
        </Arbeitsdialog>
      )}
    </>
  );
}

/**
 * Die KREDITORNUMMER einer Beziehung (Spec „Nummern an der Beziehung“, Entscheid 497) — nur mit Lieferantenrolle
 * (der Server weist sonst 422 `KEINE_LIEFERANTENROLLE` ab). „Nächste freie“ zieht aus dem Kreditorenkreis nach einer
 * gestapelten Bestätigung; „Kreditornummer eintragen“ nimmt eine von Hand im Modalpanel (nur Ziffern, sonst 422
 * `KREDITOR_UNGUELTIG`; vergeben → 409 `KREDITOR_VERGEBEN`, beides am Feld). Liegt die Nummer ausserhalb des Kreises,
 * speichert der Server sie und antwortet mit einer Warnung — sie steht danach an der Nummer.
 */
export function CreditorNumber(p: RelationView): ReactElement | null {
  const r = p.relation;
  const [dialog, setDialog] = useState<{ wert: string; urspruenglich: string } | null>(null);
  const [warnung, setWarnung] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [naechsteLaeuft, setNaechsteLaeuft] = useState(false);
  const mounted = useMounted();
  const { bestaetigen } = useBestaetigung();
  const formular = useFormularZustand<{ akte: KundeAkte; warning: string | null } | null>({
    speichern: async () => {
      if (dialog === null) return null;
      const wert = dialog.wert.trim();
      const antwort = await fieldErrorInsteadOf409(() => p.client.setKreditornummer(p.kunde.id, r.id,
        { modus: "WERT", creditorNumber: wert === "" ? null : wert }), ["KREDITOR_VERGEBEN"]);
      return { akte: await p.client.getCustomer(p.kunde.id), warning: antwort.warning };
    },
    neuLaden: async () => { /* kein Versionsvergleich auf diesem Weg */ },
    bekannteFelder: ["creditorNumber"],
  });
  const geaendert = dialog !== null && dialog.wert !== dialog.urspruenglich;
  useBeforeUnload(geaendert);
  if (r.supplierStatus == null) return null;

  async function naechste(): Promise<void> {
    if (naechsteLaeuft) return;
    if (!(await bestaetigen({ titel: PARTNER_TEXT.confirmCreditorNext(p.kunde.companyName),
      text: PARTNER_TEXT.creditorNextText(r.companyName, r.creditorNumber),
      bestaetigenText: PARTNER_TEXT.creditorAssign, gefaehrlich: r.creditorNumber !== null }))) return;
    setNaechsteLaeuft(true);
    setMeldung(null);
    try {
      const antwort = await p.client.setKreditornummer(p.kunde.id, r.id, { modus: "NAECHSTE" });
      const akte = await p.client.getCustomer(p.kunde.id);
      if (!mounted.current) return;
      setWarnung(antwort.warning);
      p.onAktualisiert(akte);
    } catch (fehler) {
      if (mounted.current) setMeldung(fehler instanceof ApiError ? fehler.message : PARTNER_TEXT.creditorFailed);
    } finally {
      if (mounted.current) setNaechsteLaeuft(false);
    }
  }

  function oeffnen(): void {
    const wert = r.creditorNumber ?? "";
    setDialog({ wert, urspruenglich: wert });
    setMeldung(null);
    formular.zuruecksetzen();
  }

  async function abbrechen(): Promise<void> {
    if (formular.laeuft) return;
    if (!(await darfAbbrechen(geaendert))) return;
    setDialog(null);
  }

  async function eintragen(): Promise<void> {
    const ergebnis = await formular.aktion();
    if (ergebnis === undefined || ergebnis === null || !mounted.current) return;
    setDialog(null);
    setWarnung(ergebnis.warning);
    p.onAktualisiert(ergebnis.akte);
  }

  return (
    <>
      <span className="kbz__kreditor">
        {r.creditorNumber === null ? PARTNER_TEXT.creditorNone : PARTNER_TEXT.creditorValue(r.creditorNumber)}
      </span>
      <span className="kbz__aktionen">
        <KiKnopf deaktiviert={naechsteLaeuft} onClick={() => { void naechste(); }}>{PARTNER_TEXT.creditorNext}</KiKnopf>
        <KiKnopf onClick={oeffnen}>{PARTNER_TEXT.creditorEnter}</KiKnopf>
      </span>
      {warnung !== null && <Hinweis art="warnung">{warnung}</Hinweis>}
      {meldung !== null && <Hinweis art="fehler">{meldung}</Hinweis>}
      {dialog !== null && (
        <Modal titel={PARTNER_TEXT.creditorDialog(r.companyName)} onAbbrechen={abbrechen} abbrechenGesperrt={formular.laeuft}>
          {formular.meldung !== null && <Hinweis art="fehler">{formular.meldung}</Hinweis>}
          <Formular beschriftung="oben" {...formular.formularProps}>
            <Feld id={`prk-${r.id}`} label={PARTNER_TEXT.creditor} bearbeitbar wert={dialog.wert}
              fehler={formular.feldFehler.creditorNumber}
              onChange={(wert) => setDialog((d) => (d === null ? d : { ...d, wert }))} />
          </Formular>
          <ModalFuss speichernText={PARTNER_TEXT.save} speichernGesperrt={formular.laeuft}
            abbrechenGesperrt={formular.laeuft} onSpeichern={() => { void eintragen(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}
    </>
  );
}
